/**
 * CallParticipant - Manages individual participant in a multi-peer call
 * Handles participant state, media streams, and peer connections
 */

import { CallMediaStream } from "./CallMediaStream";
import {
  CallPeerConnection,
  type PeerConnectionEvents,
} from "./CallPeerConnection";
import { useCallStore, type CallStore } from "../store";
import { BaseStore } from "./BaseStore";
import type {
  CallParticipant as ICallParticipant,
  PeerConnectionConfig,
} from "../types";

export class CallParticipant extends BaseStore<CallStore> {
  private userId: string;
  private mediaStream: CallMediaStream | null = null;
  private peerConnections = new Map<string, CallPeerConnection>();
  private remoteStreams = new Map<string, MediaStream>();
  private isLocalParticipant: boolean = false;
  private callbacks: {
    onStateChanged?: (userId: string, state: Partial<ICallParticipant>) => void;
    onStreamReceived?: (fromUserId: string, stream: MediaStream) => void;
    onPeerConnected?: (userId: string) => void;
    onPeerDisconnected?: (userId: string) => void;
    onSignal?: (fromUserId: string, toUserId: string, signal: any) => void;
    onError?: (error: Error) => void;
  };

  constructor(
    userId: string,
    callbacks: CallParticipant["callbacks"] = {},
    isLocalParticipant: boolean = false
  ) {
    super(useCallStore);
    this.userId = userId;
    this.callbacks = callbacks;
    this.isLocalParticipant = isLocalParticipant;

    // Initialize participant state in store
    const initialState: ICallParticipant = {
      userId,
      videoOn: true,
      audioOn: true,
      screenSharing: false,
    };

    // Add to store immediately
    if (this.isLocalParticipant) {
      this.state.setLocalParticipant(initialState);
    }
    this.state.addParticipant(initialState);
  }

  /**
   * Initialize participant with media stream
   */
  async initialize(constraints?: {
    video: boolean;
    audio: boolean;
  }): Promise<void> {
    try {
      console.log(`[CallParticipant] Initializing participant ${this.userId}`);

      this.mediaStream = new CallMediaStream(constraints);
      const stream = await this.mediaStream.initialize();

      const updates = {
        stream,
        videoElement: this.mediaStream.createVideoElement(),
      };

      this.updateState(updates);

      console.log(
        `[CallParticipant] Participant ${this.userId} initialized successfully`
      );
    } catch (error) {
      console.error(
        `[CallParticipant] Failed to initialize participant ${this.userId}:`,
        error
      );
      this.callbacks.onError?.(error as Error);
      throw error;
    }
  }

  /**
   * Create peer connection to another participant
   */
  async createPeerConnection(
    remoteUserId: string,
    config: PeerConnectionConfig,
    isInitiator: boolean
  ): Promise<void> {
    try {
      console.log(
        `[CallParticipant] Creating peer connection from ${this.userId} to ${remoteUserId}`
      );

      if (this.peerConnections.has(remoteUserId)) {
        console.warn(
          `[CallParticipant] Peer connection to ${remoteUserId} already exists`
        );
        return;
      }

      const peerEvents: Partial<PeerConnectionEvents> = {
        onSignal: (data) => {
          console.log(
            `[CallParticipant] Signal from ${this.userId} to ${remoteUserId}:`,
            data.type
          );
          // Forward signal to the signaling server via the provided callback
          this.callbacks.onSignal?.(this.userId, remoteUserId, data);
        },
        onConnect: () => {
          console.log(
            `[CallParticipant] ${this.userId} connected to ${remoteUserId}`
          );
          this.callbacks.onPeerConnected?.(remoteUserId);
        },
        onStream: (stream) => {
          console.log(
            `[CallParticipant] Stream received from ${remoteUserId} by ${this.userId}`
          );
          this.remoteStreams.set(remoteUserId, stream);

          // Update the remote participant's state in the store with their stream
          this.state.updateParticipant(remoteUserId, { stream });

          this.callbacks.onStreamReceived?.(remoteUserId, stream);
        },
        onClose: () => {
          console.log(
            `[CallParticipant] Connection closed between ${this.userId} and ${remoteUserId}`
          );
          this.remoteStreams.delete(remoteUserId);

          // Remove the stream from the remote participant's state in the store
          this.state.updateParticipant(remoteUserId, { stream: undefined });

          this.callbacks.onPeerDisconnected?.(remoteUserId);
        },
        onError: (error) => {
          console.error(
            `[CallParticipant] Peer connection error between ${this.userId} and ${remoteUserId}:`,
            error
          );
          this.callbacks.onError?.(error);
        },
      };

      const peerConnection = new CallPeerConnection(
        remoteUserId,
        { ...config, initiator: isInitiator },
        peerEvents
      );

      await peerConnection.createConnection(this.getCurrentStream());
      this.peerConnections.set(remoteUserId, peerConnection);
    } catch (error) {
      console.error(
        `[CallParticipant] Failed to create peer connection from ${this.userId} to ${remoteUserId}:`,
        error
      );
      this.callbacks.onError?.(error as Error);
      throw error;
    }
  }

  /**
   * Get peer connection to specific user
   */
  getPeerConnection(remoteUserId: string): CallPeerConnection | undefined {
    return this.peerConnections.get(remoteUserId);
  }

  /**
   * Signal to a specific peer
   */
  signalToPeer(remoteUserId: string, data: any): void {
    const peerConnection = this.peerConnections.get(remoteUserId);
    if (peerConnection) {
      peerConnection.signal(data);
    } else {
      console.warn(
        `[CallParticipant] No peer connection found for ${remoteUserId}`
      );
    }
  }

  /**
   * Toggle audio for this participant
   */
  toggleAudio(enabled: boolean): void {
    if (this.mediaStream) {
      this.mediaStream.toggleAudio(enabled);
      this.updateState({ audioOn: enabled });
    }
  }

  /**
   * Toggle video for this participant
   */
  toggleVideo(enabled: boolean): void {
    if (this.mediaStream) {
      this.mediaStream.toggleVideo(enabled);
      this.updateState({ videoOn: enabled });
    }
  }

  /**
   * Start screen sharing
   */
  async startScreenShare(): Promise<void> {
    try {
      if (!this.mediaStream) {
        throw new Error("Media stream not initialized");
      }

      console.log(`[CallParticipant] Starting screen share for ${this.userId}`);

      const screenStream = await this.mediaStream.startScreenShare();

      // Update all peer connections with the new stream
      for (const [remoteUserId, peerConnection] of this.peerConnections) {
        peerConnection.addStream(screenStream);
        console.log(
          `[CallParticipant] Screen share stream added to connection with ${remoteUserId}`
        );
      }

      this.updateState({
        screenSharing: true,
        stream: screenStream,
      });

      console.log(
        `[CallParticipant] Screen sharing started for ${this.userId}`
      );
    } catch (error) {
      console.error(
        `[CallParticipant] Failed to start screen share for ${this.userId}:`,
        error
      );
      this.callbacks.onError?.(error as Error);
      throw error;
    }
  }

  /**
   * Stop screen sharing
   */
  async stopScreenShare(): Promise<void> {
    try {
      if (!this.mediaStream) {
        throw new Error("Media stream not initialized");
      }

      console.log(`[CallParticipant] Stopping screen share for ${this.userId}`);

      const cameraStream = await this.mediaStream.stopScreenShare();

      // Update all peer connections with the camera stream
      for (const [remoteUserId, peerConnection] of this.peerConnections) {
        peerConnection.addStream(cameraStream);
        console.log(
          `[CallParticipant] Camera stream restored for connection with ${remoteUserId}`
        );
      }

      this.updateState({
        screenSharing: false,
        stream: cameraStream,
      });

      console.log(
        `[CallParticipant] Screen sharing stopped for ${this.userId}`
      );
    } catch (error) {
      console.error(
        `[CallParticipant] Failed to stop screen share for ${this.userId}:`,
        error
      );
      this.callbacks.onError?.(error as Error);
      throw error;
    }
  }

  /**
   * Get current participant state
   */
  getState(): ICallParticipant {
    const storeState = this.state;
    if (this.isLocalParticipant) {
      return (
        storeState.localParticipant || {
          userId: this.userId,
          videoOn: true,
          audioOn: true,
          screenSharing: false,
        }
      );
    }
    return (
      storeState.participants[this.userId] || {
        userId: this.userId,
        videoOn: true,
        audioOn: true,
        screenSharing: false,
      }
    );
  }

  /**
   * Get participant's user ID
   */
  getUserId(): string {
    return this.userId;
  }

  /**
   * Get participant's media stream
   */
  getMediaStream(): CallMediaStream | null {
    return this.mediaStream;
  }

  /**
   * Get participant's current stream
   */
  getCurrentStream(): MediaStream | undefined {
    return this.getState().stream;
  }

  /**
   * Get participant's video element
   */
  getVideoElement(): HTMLVideoElement | undefined {
    return this.getState().videoElement;
  }

  /**
   * Get remote stream from specific participant
   */
  getRemoteStream(fromUserId: string): MediaStream | undefined {
    return this.remoteStreams.get(fromUserId);
  }

  /**
   * Get all remote streams
   */
  getAllRemoteStreams(): Map<string, MediaStream> {
    return new Map(this.remoteStreams);
  }

  /**
   * Get all peer connections
   */
  getAllPeerConnections(): Map<string, CallPeerConnection> {
    return new Map(this.peerConnections);
  }

  /**
   * Get connection status with all peers
   */
  getConnectionStatus(): Record<string, boolean> {
    const status: Record<string, boolean> = {};

    for (const [userId, peerConnection] of this.peerConnections) {
      status[userId] = peerConnection.isConnected();
    }

    return status;
  }

  /**
   * Remove peer connection
   */
  removePeerConnection(remoteUserId: string): void {
    const peerConnection = this.peerConnections.get(remoteUserId);

    if (peerConnection) {
      peerConnection.destroy();
      this.peerConnections.delete(remoteUserId);
      this.remoteStreams.delete(remoteUserId);

      // Remove the stream from the remote participant's state in the store
      this.state.updateParticipant(remoteUserId, { stream: undefined });

      console.log(
        `[CallParticipant] Removed peer connection from ${this.userId} to ${remoteUserId}`
      );
    }
  }

  /**
   * Update participant state and notify store and callbacks
   */
  private updateState(updates: Partial<ICallParticipant>): void {
    const previousState = this.getState();

    // Only notify if there are actual changes
    const hasChanges = Object.keys(updates).some(
      (key) =>
        previousState[key as keyof ICallParticipant] !==
        updates[key as keyof ICallParticipant]
    );

    if (hasChanges) {
      // Update store
      const newState = { ...previousState, ...updates };

      if (this.isLocalParticipant) {
        this.state.setLocalParticipant(newState);
      }
      this.state.updateParticipant(this.userId, updates);

      // Notify callbacks
      this.callbacks.onStateChanged?.(this.userId, updates);
    }
  }

  /**
   * Get participant info for debugging
   */
  getDebugInfo() {
    return {
      userId: this.userId,
      state: this.getState(),
      peerConnections: Array.from(this.peerConnections.keys()),
      remoteStreams: Array.from(this.remoteStreams.keys()),
      connectionStatus: this.getConnectionStatus(),
      mediaStreamInfo: this.mediaStream?.getStreamInfo(),
    };
  }

  /**
   * Cleanup participant resources
   */
  cleanup(): void {
    console.log(`[CallParticipant] Cleaning up participant ${this.userId}`);

    // Remove from store
    this.state.removeParticipant(this.userId);

    // Destroy all peer connections
    for (const peerConnection of this.peerConnections.values()) {
      peerConnection.destroy();
    }
    this.peerConnections.clear();

    // Clear remote streams
    this.remoteStreams.clear();

    // Cleanup media stream
    if (this.mediaStream) {
      this.mediaStream.cleanup();
      this.mediaStream = null;
    }

    console.log(
      `[CallParticipant] Participant ${this.userId} cleanup completed`
    );
  }
}
